import { Button, Form, Input, Modal, Select, Switch, Table, message } from 'antd';
import { useEffect, useState } from 'react';
import { EmptyState } from '../../components/EmptyState';
import { consentLabel } from '../../lib/status';
import { showcasesApi, statsApi } from '../../services/resources';
import type { ActivityCounts, Showcase } from '../../types/api';

export function AdminShowcasesPage() {
  const [items, setItems] = useState<Showcase[]>([]);
  const [counts, setCounts] = useState<ActivityCounts | null>(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');

  async function load() {
    try {
      const [s, c] = await Promise.all([showcasesApi.list({ page: 1, limit: 50 }), statsApi.counts().catch(() => null)]);
      setItems(s.items);
      setCounts(c);
      setError('');
    } catch {
      setError('成果列表失败');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  return (
    <div className="stack">
      <h1 className="page-title">成果管理</h1>
      {counts ? <p className="meta">开展次数（系统去重）{counts.corrected_count}，禁止按占用行数累加。</p> : null}
      <Button type="primary" className="touch" onClick={() => setOpen(true)}>
        新建成果
      </Button>
      {error ? (
        <EmptyState text={error} kind="image" />
      ) : items.length === 0 ? (
        <EmptyState text="还没有成果条目。可直接维护并发布，不必等教师草稿。" kind="image" />
      ) : (
        <Table
          scroll={{ x: 760 }}
          rowKey="id"
          dataSource={items}
          pagination={false}
          columns={[
            { title: '标题', dataIndex: 'title' },
            { title: '可见性', dataIndex: 'visibility' },
            { title: '授权', render: (_, r) => consentLabel(r.consent_status) },
            { title: '状态', dataIndex: 'status' },
            {
              title: '操作',
              render: (_, r) => (
                <Button
                  className="touch"
                  onClick={() => {
                    if (r.status === 'published') {
                      Modal.confirm({
                        title: '下架成果',
                        content: '本次下架将写入操作人、时间、对象与原因',
                        onOk: async () => {
                          await showcasesApi.unpublish(r.id);
                          await load();
                        },
                      });
                    } else {
                      void showcasesApi.publish(r.id).then(() => load());
                    }
                  }}
                >
                  {r.status === 'published' ? '下架' : '发布'}
                </Button>
              ),
            },
          ]}
        />
      )}
      <CreateModal open={open} onClose={() => setOpen(false)} onOk={() => void load()} />
    </div>
  );
}

function CreateModal(props: { open: boolean; onClose: () => void; onOk: () => void }) {
  const [form] = Form.useForm();
  return (
    <Modal
      title="新建成果"
      open={props.open}
      onCancel={props.onClose}
      onOk={async () => {
        const v = await form.validateFields();
        await showcasesApi.create(v);
        message.success('已保存');
        props.onOk();
        props.onClose();
      }}
    >
      <Form form={form} layout="vertical" initialValues={{ visibility: 'school_only', contains_minors: true }}>
        <Form.Item name="title" label="标题" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="summary" label="摘要">
          <Input.TextArea />
        </Form.Item>
        <Form.Item name="visibility" label="可见性">
          <Select
            options={[
              { value: 'school_only', label: '本校登录（默认）' },
              { value: 'campus_network_readonly', label: '校园网只读（需显式开启）' },
              { value: 'internet_public', label: '互联网公开（默认关）' },
            ]}
          />
        </Form.Item>
        <Form.Item name="contains_minors" label="含未成年人" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
}
